import React, { useEffect, useState } from "react";
import "./Report.css";
import ReactApexChart from "react-apexcharts";
import Loader from "../../Loader/Loader";
import ListControls from "../ListControls/ListControls";
import Pagination from "../Pagination/pagination";

import {
  getSalesReport,
  getCustomerReport,
  getDriverReport,
  getStoreReport,
  getOperationReport,
  exportReport,
} from "../../service/Mainservice";

import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import DownloadIcon from "@mui/icons-material/Download";

const REPORT_SORT_OPTIONS = {
  sales: [{value:"revenue",label:"Revenue"},{value:"orderCount",label:"Order count"},{value:"period",label:"Period"},{value:"shopName",label:"Store"}],
  customers: [{value:"orderCount",label:"Order count"},{value:"totalSpent",label:"Total spent"},{value:"customerName",label:"Customer"}],
  drivers: [{value:"deliveryCount",label:"Deliveries"},{value:"driverName",label:"Driver"},{value:"rating",label:"Rating"}],
  stores: [{value:"revenue",label:"Revenue"},{value:"orderCount",label:"Order count"},{value:"avgPrepTime",label:"Preparation time"}],
  operations: [{value:"hour",label:"Hour"},{value:"zone",label:"Zone"},{value:"orderCount",label:"Order count"},{value:"rating",label:"Rating"}],
};

const DEFAULT_SORTS = {
  sales: "revenue", customers: "orderCount", drivers: "deliveryCount",
  stores: "revenue", operations: "orderCount",
};

const REPORT_FILTER_FIELDS = {
  sales: ["startDate", "endDate", "groupBy", "vendorId", "city", "category"],
  customers: ["startDate", "endDate", "city"],
  drivers: ["startDate", "endDate", "city"],
  stores: ["startDate", "endDate", "vendorId", "city", "category"],
  operations: ["startDate", "endDate", "groupBy", "city"],
};

const Reports = () => {
  const [loading, setLoading] = useState(false);

  const [activeTab, setActiveTab] = useState("sales");
  const [sortByTab, setSortByTab] = useState(DEFAULT_SORTS);
  const [sortOrderTab, setSortOrderTab] = useState({sales:"desc",customers:"desc",drivers:"desc",stores:"desc",operations:"desc"});
  const [reportPagination, setReportPagination] = useState({});
  const activeSortBy = sortByTab[activeTab];
  const activeSortOrder = sortOrderTab[activeTab];

  const [salesData, setSalesData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [driverData, setDriverData] = useState(null);
  const [storeData, setStoreData] = useState(null);
  const [operationData, setOperationData] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    groupBy: "day",
    vendorId: "",
    city: "",
    category: "",
  });

  useEffect(() => {
    const today = new Date();

    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    setFilters((prev) => ({
      ...prev,
      startDate: firstDay.toISOString().split("T")[0],
      endDate: today.toISOString().split("T")[0],
    }));
  }, []);

  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      loadReports();
    }
  }, [activeTab, filters.startDate, filters.endDate, activeSortBy, activeSortOrder]);

  const buildReportParams = (tab, page = 1) => {
    const tabFilters = REPORT_FILTER_FIELDS[tab].reduce((params, field) => {
      if (filters[field] !== "") params[field] = filters[field];
      return params;
    }, {});

    return {
      ...tabFilters,
      page,
      pageSize: 10,
      sortBy: sortByTab[tab],
      sortOrder: sortOrderTab[tab],
    };
  };

  const loadReports = async (page = 1) => {
    try {
      setLoading(true);

      const requestByTab = {
        sales: getSalesReport,
        customers: getCustomerReport,
        drivers: getDriverReport,
        stores: getStoreReport,
        operations: getOperationReport,
      };
      const setDataByTab = {
        sales: setSalesData,
        customers: setCustomerData,
        drivers: setDriverData,
        stores: setStoreData,
        operations: setOperationData,
      };

      const response = await requestByTab[activeTab](
        buildReportParams(activeTab, page),
      );
      setDataByTab[activeTab](response.data.data);
      setReportPagination((current) => ({
        ...current,
        [activeTab]: response.data.pagination,
      }));

      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleGenerate = () => {
    loadReports();
  };

  const handleExport = async (type) => {
    try {
      setLoading(true);

      await exportReport({
        reportType: activeTab,
        format: type,
        ...buildReportParams(activeTab),
      });

      setShowExport(false);
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  /* -----------------------------
      SALES TREND CHART
  ------------------------------*/

  const salesTrendChart = {
    series: [
      {
        name: "Revenue",
        data: salesData?.trends?.map((x) => x.revenue) || [],
      },
    ],

    options: {
      chart: {
        type: "area",
        toolbar: {
          show: false,
        },
      },

      stroke: {
        curve: "smooth",
      },

      dataLabels: {
        enabled: false,
      },

      colors: ["#eb6718"],

      xaxis: {
        categories: salesData?.trends?.map((x) => x.period) || [],
      },
    },
  };

  /* -----------------------------
      STORE BAR CHART
  ------------------------------*/

  const storeChart = {
    series: [
      {
        data: salesData?.byStore?.map((x) => x.revenue) || [],
      },
    ],

    options: {
      chart: {
        type: "bar",
        toolbar: {
          show: false,
        },
      },

      colors: ["#133a34"],

      plotOptions: {
        bar: {
          borderRadius: 6,
        },
      },

      xaxis: {
        categories: salesData?.byStore?.map((x) => x.shopName) || [],
      },
    },
  }; /* -----------------------------
      CATEGORY CHART
  ------------------------------*/

  const categoryChart = {
    series: [
      {
        data: salesData?.byCategory?.map((x) => x.revenue) || [],
      },
    ],

    options: {
      chart: {
        type: "bar",
        toolbar: {
          show: false,
        },
      },

      colors: ["#589b3b"],

      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 6,
        },
      },

      dataLabels: {
        enabled: false,
      },

      xaxis: {
        categories: salesData?.byCategory?.map((x) => x.category) || [],
      },
    },
  };

  /* -----------------------------
      CUSTOMER DONUT
  ------------------------------*/

  const customerChart = {
    series: [customerData?.newUsers || 0, customerData?.returningUsers || 0],

    options: {
      labels: ["New Customers", "Returning Customers"],

      colors: ["#133a34", "#eb6718"],

      legend: {
        position: "bottom",
      },

      chart: {
        type: "donut",
      },
    },
  };

  /* -----------------------------
      DRIVER RADIAL
  ------------------------------*/

  const driverChart = {
    series: [driverData?.acceptanceRate || 0],

    options: {
      chart: {
        type: "radialBar",
      },

      colors: ["#133a34"],

      labels: ["Acceptance Rate"],

      plotOptions: {
        radialBar: {
          hollow: {
            size: "65%",
          },

          dataLabels: {
            value: {
              formatter: function (val) {
                return val + "%";
              },
            },
          },
        },
      },
    },
  };

  /* -----------------------------
      PEAK HOURS
  ------------------------------*/

  const peakChart = {
    series: [
      {
        name: "Orders",
        data: operationData?.peakHours?.map((x) => x.count) || [],
      },
    ],

    options: {
      chart: {
        type: "bar",
        toolbar: {
          show: false,
        },
      },

      colors: ["#eb6718"],

      xaxis: {
        categories: operationData?.peakHours?.map((x) => x.hour + ":00") || [],
      },
    },
  };

  /* -----------------------------
      SATISFACTION TREND
  ------------------------------*/

  const ratingChart = {
    series: [
      {
        name: "Rating",
        data: operationData?.satisfactionTrends?.map((x) => x.avgRating) || [],
      },
    ],

    options: {
      chart: {
        type: "line",
        toolbar: {
          show: false,
        },
      },

      stroke: {
        curve: "smooth",
      },

      colors: ["#589b3b"],

      xaxis: {
        categories:
          operationData?.satisfactionTrends?.map((x) => x.period) || [],
      },
    },
  };

  /* -----------------------------
      HELPERS
  ------------------------------*/

  const currency = (value) => {
    return `$${Number(value || 0).toFixed(2)}`;
  };

  const percentage = (value) => {
    return `${Number(value || 0).toFixed(1)}%`;
  };

  const emptyData = (arr) => {
    return !arr || arr.length === 0;
  };

  /* =====================================
      RETURN
  ===================================== */

  return (
    <div className="reports_page">
      {/* ================= HEADER ================= */}
      <div className="reports_header">
        <div>
          <h1>
            <AnalyticsIcon />
            Reports & Analytics
          </h1>

          <p>Sales, Customers, Drivers, Stores & Operations Reports</p>
        </div>

        <div className="export_dropdown">
          <button
            className="export_btn"
            onClick={() => setShowExport(!showExport)}
          >
            <DownloadIcon />
            Export
          </button>

          {showExport && (
            <div className="export_menu">
              <button onClick={() => handleExport("csv")}>Export CSV</button>

              <button onClick={() => handleExport("xlsx")}>Export Excel</button>
            </div>
          )}
        </div>
      </div>
      {/* ================= REPORT TABS ================= */}
      <div className="report_tabs">
        <button className={activeTab === "sales" ? "active_tab" : ""} onClick={() => setActiveTab("sales")}>
          <TrendingUpIcon /> Sales
        </button>
        <button className={activeTab === "customers" ? "active_tab" : ""} onClick={() => setActiveTab("customers")}>
          <PeopleAltIcon /> Customers
        </button>
        <button className={activeTab === "drivers" ? "active_tab" : ""} onClick={() => setActiveTab("drivers")}>
          <LocalShippingIcon /> Drivers
        </button>
        <button className={activeTab === "stores" ? "active_tab" : ""} onClick={() => setActiveTab("stores")}>
          <StorefrontIcon /> Stores
        </button>
        <button className={activeTab === "operations" ? "active_tab" : ""} onClick={() => setActiveTab("operations")}>
          <AnalyticsIcon /> Operations
        </button>
      </div>
      {/* ================= FILTERS ================= */}
      <div className="filter_card">
        <div className="filter_grid">
          <div>
            <label>Start Date</label>

            <input
              type="date"
              value={filters.startDate}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  startDate: e.target.value,
                })
              }
            />
          </div>

          <div>
            <label>End Date</label>

            <input
              type="date"
              value={filters.endDate}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  endDate: e.target.value,
                })
              }
            />
          </div>

          {(activeTab === "sales" || activeTab === "operations") && (
            <div>
              <label>Group By</label>

            <select
              value={filters.groupBy}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  groupBy: e.target.value,
                })
              }
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
            </select>
            </div>
          )}

          <div>
            <label>City</label>

            <input
              placeholder="New York"
              value={filters.city}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  city: e.target.value,
                })
              }
            />
          </div>

          {(activeTab === "sales" || activeTab === "stores") && (
            <div>
              <label>Category</label>

            <input
              placeholder="Food"
              value={filters.category}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  category: e.target.value,
                })
              }
            />
            </div>
          )}

          {(activeTab === "sales" || activeTab === "stores") && (
            <div>
              <label>Store ID</label>

            <input
              value={filters.vendorId}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  vendorId: e.target.value,
                })
              }
            />
            </div>
          )}
        </div>

        <button className="generate_btn" onClick={handleGenerate}>
          Generate Report
        </button>
      </div>{" "}
      <ListControls
        sortOptions={REPORT_SORT_OPTIONS[activeTab]}
        sortBy={sortByTab[activeTab]}
        sortOrder={sortOrderTab[activeTab]}
        onSortByChange={(value) => setSortByTab((current) => ({ ...current, [activeTab]: value }))}
        onSortOrderChange={(value) => setSortOrderTab((current) => ({ ...current, [activeTab]: value }))}
      />
      {/* ==========================================================
                          SALES TAB
      ========================================================== */}
      {activeTab === "sales" && (
        <>
          {/* ---------- SUMMARY CARDS ---------- */}

          <div className="summary_grid">
            <div className="summary_card">
              <AttachMoneyIcon />
              <div>
                <span>Total Revenue</span>
                <h2>{currency(salesData?.summary?.totalRevenue)}</h2>
              </div>
            </div>

            <div className="summary_card">
              <ShoppingBagIcon />
              <div>
                <span>Total Orders</span>
                <h2>{salesData?.summary?.totalOrders || 0}</h2>
              </div>
            </div>

            <div className="summary_card">
              <TrendingUpIcon />
              <div>
                <span>Net Profit</span>
                <h2>{currency(salesData?.summary?.netProfit)}</h2>
              </div>
            </div>

            <div className="summary_card">
              <AttachMoneyIcon />
              <div>
                <span>Average Order</span>
                <h2>{currency(salesData?.summary?.avgOrderValue)}</h2>
              </div>
            </div>

            <div className="summary_card">
              <AttachMoneyIcon />
              <div>
                <span>Commission</span>
                <h2>{currency(salesData?.summary?.commissionEarned)}</h2>
              </div>
            </div>

            <div className="summary_card">
              <AttachMoneyIcon />
              <div>
                <span>Delivery Fees</span>
                <h2>{currency(salesData?.summary?.deliveryFees)}</h2>
              </div>
            </div>

            <div className="summary_card">
              <AttachMoneyIcon />
              <div>
                <span>Service Fees</span>
                <h2>{currency(salesData?.summary?.serviceFees)}</h2>
              </div>
            </div>

            <div className="summary_card">
              <AttachMoneyIcon />
              <div>
                <span>Refunds</span>
                <h2>{currency(salesData?.summary?.refundsIssued)}</h2>
              </div>
            </div>
          </div>

          {/* ---------- CHARTS ---------- */}

          <div className="chart_grid">
            <div className="chart_card">
              <div className="card_title">Revenue Trend</div>

              {emptyData(salesData?.trends) ? (
                <div className="empty_chart">No revenue trend available</div>
              ) : (
                <ReactApexChart
                  options={salesTrendChart.options}
                  series={salesTrendChart.series}
                  type="area"
                  height={320}
                />
              )}
            </div>

            <div className="chart_card">
              <div className="card_title">Revenue by Store</div>

              {emptyData(salesData?.byStore) ? (
                <div className="empty_chart">No store revenue available</div>
              ) : (
                <ReactApexChart
                  options={storeChart.options}
                  series={storeChart.series}
                  type="bar"
                  height={320}
                />
              )}
            </div>
          </div>

          {/* ---------- CATEGORY + CITY ---------- */}

          <div className="chart_grid">
            <div className="chart_card">
              <div className="card_title">Revenue by Category</div>

              {emptyData(salesData?.byCategory) ? (
                <div className="empty_chart">No category data available</div>
              ) : (
                <ReactApexChart
                  options={categoryChart.options}
                  series={categoryChart.series}
                  type="bar"
                  height={320}
                />
              )}
            </div>

            <div className="table_card">
              <div className="card_title">Revenue by City</div>

              {emptyData(salesData?.byCity) ? (
                <div className="empty_chart">No city revenue available</div>
              ) : (
                <table className="report_table">
                  <thead>
                    <tr>
                      <th>City</th>
                      <th>Revenue</th>
                      <th>Orders</th>
                    </tr>
                  </thead>

                  <tbody>
                    {salesData.byCity.map((city, index) => (
                      <tr key={index}>
                        <td>{city.city}</td>

                        <td>{currency(city.revenue)}</td>

                        <td>{city.orders}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
      {/* ==========================================================
                    CUSTOMERS TAB
========================================================== */}
      {activeTab === "customers" && (
        <>
          {/* CUSTOMER SUMMARY */}

          <div className="summary_grid">
            <div className="summary_card">
              <PeopleAltIcon />

              <div>
                <span>New Customers</span>
                <h2>{customerData?.newUsers || 0}</h2>
              </div>
            </div>

            <div className="summary_card">
              <PeopleAltIcon />

              <div>
                <span>Returning Customers</span>

                <h2>{customerData?.returningUsers || 0}</h2>
              </div>
            </div>

            <div className="summary_card">
              <AttachMoneyIcon />

              <div>
                <span>Customer Lifetime Value</span>

                <h2>{currency(customerData?.customerLifetimeValue)}</h2>
              </div>
            </div>

            <div className="summary_card danger_card">
              <TrendingUpIcon />

              <div>
                <span>Churn Rate</span>

                <h2>{percentage(customerData?.churnRate)}</h2>
              </div>
            </div>
          </div>

          {/* CUSTOMER CHART + INFO */}

          <div className="chart_grid">
            <div className="chart_card">
              <div className="card_title">Customer Growth</div>

              <ReactApexChart
                options={customerChart.options}
                series={customerChart.series}
                type="donut"
                height={320}
              />
            </div>

            <div className="insight_card">
              <div className="card_title">Customer Insights</div>

              <div className="insight_row">
                <span>New Users</span>

                <strong>{customerData?.newUsers || 0}</strong>
              </div>

              <div className="insight_row">
                <span>Returning Users</span>

                <strong>{customerData?.returningUsers || 0}</strong>
              </div>

              <div className="insight_row">
                <span>Lifetime Value</span>

                <strong>{currency(customerData?.customerLifetimeValue)}</strong>
              </div>

              <div className="insight_row">
                <span>Churn Rate</span>

                <strong className="danger_text">
                  {percentage(customerData?.churnRate)}
                </strong>
              </div>
            </div>
          </div>

          {/* TOP CUSTOMERS TABLE */}

          <div className="table_card">
            <div className="card_title">Top Customers</div>

            {emptyData(customerData?.topCustomers) ? (
              <div className="empty_chart">No customer data available</div>
            ) : (
              <table className="report_table">
                <thead>
                  <tr>
                    <th>Customer</th>

                    <th>Orders</th>

                    <th>Total Spent</th>
                  </tr>
                </thead>

                <tbody>
                  {customerData.topCustomers.map((customer, index) => (
                    <tr key={index}>
                      <td>{customer.fullName || customer.name || "-"}</td>

                      <td>{customer.orderCount || customer.orders || 0}</td>

                      <td>{currency(customer.totalSpent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
      {/* ==========================================================
                    DRIVERS TAB
========================================================== */}
      {activeTab === "drivers" && (
        <>
          {/* DRIVER SUMMARY */}

          <div className="summary_grid">
            <div className="summary_card">
              <AttachMoneyIcon />

              <div>
                <span>Avg Earnings / Driver</span>

                <h2>{currency(driverData?.averageEarningsPerDriver)}</h2>
              </div>
            </div>

            <div className="summary_card">
              <TrendingUpIcon />

              <div>
                <span>Acceptance Rate</span>

                <h2>{percentage(driverData?.acceptanceRate)}</h2>
              </div>
            </div>

            <div className="summary_card danger_card">
              <TrendingUpIcon />

              <div>
                <span>Cancellation Rate</span>

                <h2>{percentage(driverData?.cancellationRate)}</h2>
              </div>
            </div>

            <div className="summary_card">
              <LocalShippingIcon />

              <div>
                <span>Avg Delivery Time</span>

                <h2>
                  {driverData?.averageDeliveryTimeMinutes || 0}

                  <small>min</small>
                </h2>
              </div>
            </div>
          </div>

          {/* DRIVER CHARTS */}

          <div className="chart_grid">
            <div className="chart_card">
              <div className="card_title">Driver Acceptance</div>

              <ReactApexChart
                options={driverChart.options}
                series={driverChart.series}
                type="radialBar"
                height={320}
              />
            </div>

            <div className="insight_card">
              <div className="card_title">Driver Performance</div>

              <div className="insight_row">
                <span>Acceptance Rate</span>

                <strong>{percentage(driverData?.acceptanceRate)}</strong>
              </div>

              <div className="insight_row">
                <span>Cancellation Rate</span>

                <strong className="danger_text">
                  {percentage(driverData?.cancellationRate)}
                </strong>
              </div>

              <div className="insight_row">
                <span>Delivery Time</span>

                <strong>
                  {driverData?.averageDeliveryTimeMinutes || 0} min
                </strong>
              </div>
            </div>
          </div>

          {/* MOST ACTIVE DRIVERS */}

          <div className="table_card">
            <div className="card_title">Most Active Drivers</div>

            {emptyData(driverData?.mostActiveDrivers) ? (
              <div className="empty_chart">No active driver data available</div>
            ) : (
              <table className="report_table">
                <thead>
                  <tr>
                    <th>Driver</th>

                    <th>Orders</th>

                    <th>Earnings</th>
                  </tr>
                </thead>

                <tbody>
                  {driverData.mostActiveDrivers.map((driver, index) => (
                    <tr key={index}>
                      <td>{driver.fullName || driver.name || "-"}</td>

                      <td>{driver.deliveryCount || driver.orders || 0}</td>

                      <td>{currency(driver.earnings)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* LEAST ACTIVE DRIVERS */}

          <div className="table_card">
            <div className="card_title">Least Active Drivers</div>

            {emptyData(driverData?.leastActiveDrivers) ? (
              <div className="empty_chart">
                No inactive driver data available
              </div>
            ) : (
              <table className="report_table">
                <thead>
                  <tr>
                    <th>Driver</th>

                    <th>Orders</th>

                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {driverData.leastActiveDrivers.map((driver, index) => (
                    <tr key={index}>
                      <td>{driver.fullName || driver.name || "-"}</td>

                      <td>{driver.deliveryCount || driver.orders || 0}</td>

                      <td>
                        <span className="status_badge status_pending">
                          Low Activity
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
      {/* ==========================================================
                    STORES TAB
========================================================== */}
      {activeTab === "stores" && (
        <>
          {/* STORE SUMMARY */}

          <div className="summary_grid">
            <div className="summary_card">
              <StorefrontIcon />

              <div>
                <span>Top Performing Stores</span>

                <h2>{storeData?.topStores?.length || 0}</h2>
              </div>
            </div>

            <div className="summary_card danger_card">
              <StorefrontIcon />

              <div>
                <span>Underperforming Stores</span>

                <h2>{storeData?.underperformingStores?.length || 0}</h2>
              </div>
            </div>

            <div className="summary_card">
              <LocalShippingIcon />

              <div>
                <span>Stores With Prep Time</span>

                <h2>{storeData?.storePrepTimes?.length || 0}</h2>
              </div>
            </div>

            <div className="summary_card">
              <AnalyticsIcon />

              <div>
                <span>Store Performance</span>

                <h2>
                  {(storeData?.topStores?.length || 0) +
                    (storeData?.underperformingStores?.length || 0)}
                </h2>
              </div>
            </div>
          </div>

          {/* TOP STORE TABLE */}

          <div className="table_card">
            <div className="card_title">Top Performing Stores</div>

            {emptyData(storeData?.topStores) ? (
              <div className="empty_chart">No top store data available</div>
            ) : (
              <table className="report_table">
                <thead>
                  <tr>
                    <th>Store</th>

                    <th>Orders</th>

                    <th>Revenue</th>
                  </tr>
                </thead>

                <tbody>
                  {storeData.topStores.map((store, index) => (
                    <tr key={index}>
                      <td>
                        {store.shopName || store.storeName || store.name || "-"}
                      </td>

                      <td>{store.orderCount || store.orders || 0}</td>

                      <td>{currency(store.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* UNDERPERFORMING STORE TABLE */}

          <div className="table_card">
            <div className="card_title">Underperforming Stores</div>

            {emptyData(storeData?.underperformingStores) ? (
              <div className="empty_chart">No underperforming stores found</div>
            ) : (
              <table className="report_table">
                <thead>
                  <tr>
                    <th>Store</th>

                    <th>Orders</th>

                    <th>Revenue</th>

                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {storeData.underperformingStores.map((store, index) => (
                    <tr key={index}>
                      <td>
                        {store.shopName || store.storeName || store.name || "-"}
                      </td>

                      <td>{store.orderCount || store.orders || 0}</td>

                      <td>{currency(store.revenue)}</td>

                      <td>
                        <span className="status_badge status_cancelled">
                          Low Performance
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* PREP TIME TABLE */}

          <div className="table_card">
            <div className="card_title">Average Store Preparation Time</div>

            {emptyData(storeData?.storePrepTimes) ? (
              <div className="empty_chart">
                No preparation time data available
              </div>
            ) : (
              <table className="report_table">
                <thead>
                  <tr>
                    <th>Store</th>

                    <th>Average Prep Time</th>
                  </tr>
                </thead>

                <tbody>
                  {storeData.storePrepTimes.map((store, index) => (
                    <tr key={index}>
                      <td>
                        {store.shopName || store.storeName || store.name || "-"}
                      </td>

                      <td>
                        {store.avgPrepTimeMinutes || store.prepTime || 0} min
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
      {/* ==========================================================
                    OPERATIONS TAB
========================================================== */}
      {activeTab === "operations" && (
        <>
          {/* SUMMARY */}

          <div className="summary_grid">
            <div className="summary_card">
              <LocalShippingIcon />

              <div>
                <span>Average Delivery Time</span>

                <h2>
                  {operationData?.averageDeliveryTimeMinutes || 0}
                  <small> min</small>
                </h2>
              </div>
            </div>

            <div className="summary_card">
              <AnalyticsIcon />

              <div>
                <span>Peak Hours</span>

                <h2>{operationData?.peakHours?.length || 0}</h2>
              </div>
            </div>

            <div className="summary_card">
              <PeopleAltIcon />

              <div>
                <span>Busiest Zones</span>

                <h2>{operationData?.busiestZones?.length || 0}</h2>
              </div>
            </div>

            <div className="summary_card">
              <TrendingUpIcon />

              <div>
                <span>Rating History</span>

                <h2>{operationData?.satisfactionTrends?.length || 0}</h2>
              </div>
            </div>
          </div>

          {/* CHARTS */}

          <div className="chart_grid">
            <div className="chart_card">
              <div className="card_title">Peak Order Hours</div>

              {emptyData(operationData?.peakHours) ? (
                <div className="empty_chart">No peak hour data available</div>
              ) : (
                <ReactApexChart
                  options={peakChart.options}
                  series={peakChart.series}
                  type="bar"
                  height={320}
                />
              )}
            </div>

            <div className="chart_card">
              <div className="card_title">Customer Satisfaction</div>

              {emptyData(operationData?.satisfactionTrends) ? (
                <div className="empty_chart">No rating history available</div>
              ) : (
                <ReactApexChart
                  options={ratingChart.options}
                  series={ratingChart.series}
                  type="line"
                  height={320}
                />
              )}
            </div>
          </div>

          {/* BUSIEST ZONES */}

          <div className="table_card">
            <div className="card_title">Busiest Delivery Zones</div>

            {emptyData(operationData?.busiestZones) ? (
              <div className="empty_chart">No busiest zone data available</div>
            ) : (
              <table className="report_table">
                <thead>
                  <tr>
                    <th>Zone</th>

                    <th>Orders</th>
                  </tr>
                </thead>

                <tbody>
                  {operationData.busiestZones.map((zone, index) => (
                    <tr key={index}>
                      <td>{zone.city || zone.name}</td>

                      <td>{zone.orderCount || zone.orders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* CUSTOMER SATISFACTION TABLE */}

          <div className="table_card">
            <div className="card_title">Rating History</div>

            {emptyData(operationData?.satisfactionTrends) ? (
              <div className="empty_chart">No ratings available</div>
            ) : (
              <table className="report_table">
                <thead>
                  <tr>
                    <th>Period</th>

                    <th>Average Rating</th>

                    <th>Total Reviews</th>
                  </tr>
                </thead>

                <tbody>
                  {operationData.satisfactionTrends.map((item, index) => (
                    <tr key={index}>
                      <td>{item.period}</td>

                      <td>⭐ {item.avgRating}</td>

                      <td>{item.reviewCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}{" "}
      {reportPagination[activeTab] && (
        <Pagination pagedata={reportPagination[activeTab]} onPageChange={loadReports} />
      )}
      {loading && <Loader />}
    </div>
  );
};

export default Reports;
